// Libraries
import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

// Utilities
import { colors, roundTwoDecimals } from 'common/utils/helpers';

// Media Queries
import mediaQueries from 'common/utils/mediaQueries';

// Components
import DonutChart from 'admin/engagement/reporting/components/OpenBudgetQuestionReport/DonutChart';
import OpenBudgetInputChart from '../OpenBudgetInputChart';

const OpenBudgetReport = ({ question, defaultLanguage }) => {
  const { t } = useTranslation();
  const chartRef = createRef();
  const {
    data: { result, analysis },
    displayData: { comparisonValueLabel },
  } = question;
  const isEmpty = result.length === 0;
  const isSmallDevice = useMediaQuery(mediaQueries.smallDevices);
  const isMobile = useMediaQuery({ ...mediaQueries.mobile, minWidth: 481 });
  const isTablet = useMediaQuery({ ...mediaQueries.tablet, minWidth: 769 });
  const isDesktop = useMediaQuery({ ...mediaQueries.desktop, maxWidth: 1215 });
  const isWidescreen = useMediaQuery({ ...mediaQueries.widescreen, maxWidth: 1407 });

  const addAverage = (total, dataPoint) => total + Number(dataPoint.average);
  const total = analysis?.reduce(addAverage, 0);
  const data = analysis?.map((dataPoint, idx) => ({
    label: JSON.parse(dataPoint.labelTranslations)[defaultLanguage],
    color: colors[idx % colors.length],
    average: roundTwoDecimals(dataPoint.average),
    percentage: roundTwoDecimals((dataPoint.average * 100) / total),
  }));

  const renderInputCharts = () =>
    result.map((res) => (
      <OpenBudgetInputChart
        key={res.input.id}
        data={res}
        analysis={analysis}
        comparisonValueLabel={comparisonValueLabel}
      />
    ));

  const getAspectRatio = () => {
    if (isSmallDevice) return 1;
    if (isMobile) return 1.4;
    if (isTablet) return 1.8;
    if (isDesktop) return 2;
    if (isWidescreen) return 2.2;
    return 2.5;
  };

  return (
    <div className="open-budget-report">
      {isEmpty && (
        <div className="basic-report__subtitle">{t('engagement_report.no_responses')}</div>
      )}
      {!isEmpty && (
        <>
          <DonutChart data={data} chartRef={chartRef} aspect={getAspectRatio()} />
          {renderInputCharts()}
        </>
      )}
    </div>
  );
};

OpenBudgetReport.propTypes = {
  question: PropTypes.shape({
    data: PropTypes.shape({
      result: PropTypes.arrayOf(PropTypes.shape({})),
      analysis: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
        })
      ),
    }),
    displayData: PropTypes.shape({
      comparisonValueLabel: PropTypes.string,
    }),
  }).isRequired,
  defaultLanguage: PropTypes.string,
};

OpenBudgetReport.defaultProps = {
  defaultLanguage: 'en',
};

export default OpenBudgetReport;
